import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEnquiry } from "./EnquiryContext";
import { NetworkMotif, Reveal } from "./Primitives";

export const PageHero = ({ eyebrow, title, intro, image, imageAlt, children }) => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-[#F4F6F8]">
    <div className="grid-lines pointer-events-none absolute inset-0" aria-hidden="true" />
    <div className="container-x relative grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
      <Reveal className={image ? "lg:col-span-7" : "lg:col-span-9"}>
        <p className="eyebrow">{eyebrow}</p>
        <h1 className="h-display mt-6">{title}</h1>
        {intro && <p className="lead mt-8 max-w-2xl">{intro}</p>}
        {children && <div className="mt-10 flex flex-wrap gap-4">{children}</div>}
      </Reveal>
      {image && (
        <Reveal delay={0.15} className="relative lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-lg lg:aspect-[4/5]">
            <img src={image} alt={imageAlt} className="h-full w-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/40 to-transparent" />
          </div>
          <NetworkMotif className="absolute -bottom-10 -left-12 hidden w-56 lg:block" />
        </Reveal>
      )}
    </div>
  </section>
);

export const ClosingCta = ({
  title = "Complex challenges deserve exceptional people.",
  text = "Start a conversation with Trusted Leap and take the next step towards finding the right expertise for your business.",
}) => {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="relative overflow-hidden bg-[#0B1220] text-white grain" data-testid="closing-cta">
      <NetworkMotif light className="pointer-events-none absolute -right-10 top-1/2 hidden w-[560px] -translate-y-1/2 opacity-70 md:block" />
      <div className="container-x relative py-24 lg:py-32">
        <Reveal className="max-w-3xl">
          <p className="eyebrow !text-blue-300">Next step</p>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl">{title}</h2>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">{text}</p>
          <div className="mt-12 flex flex-wrap gap-4">
            <button type="button" onClick={() => openEnquiry()} className="btn-primary" data-testid="closing-request-expert-button">Request an Expert <ArrowRight className="h-4 w-4" /></button>
            <Link to="/contact" className="btn-outline-light" data-testid="closing-contact-link">Contact Us</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const RequestButton = ({ label = "Request an Expert", category = "", className = "btn-primary", testId }) => {
  const { openEnquiry } = useEnquiry();
  return (
    <button type="button" onClick={() => openEnquiry(category)} className={className} data-testid={testId}>
      {label} <ArrowRight className="h-4 w-4" />
    </button>
  );
};

export const FaqList = ({ faqs, prefix }) => (
  <dl className="divide-y divide-slate-200 border-y border-slate-200">
    {faqs.map((f, i) => (
      <details key={f.q} className="group py-6" data-testid={`${prefix}-faq-${i}`}>
        <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-display text-lg font-semibold text-[#0B1220]">
          <dt>{f.q}</dt>
          <span className="mt-1 text-xl leading-none text-[#2563EB] transition-transform group-open:rotate-45" aria-hidden="true">+</span>
        </summary>
        <dd className="mt-4 max-w-3xl leading-relaxed text-slate-600">{f.a}</dd>
      </details>
    ))}
  </dl>
);

export const NumberedList = ({ items, className = "" }) => (
  <ol className={`grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-2 ${className}`}>
    {items.map((t, i) => (
      <li key={t} className="flex gap-5 bg-white p-6">
        <span className="font-display text-sm font-semibold text-[#2563EB]">{String(i + 1).padStart(2, "0")}</span>
        <span className="text-[15px] leading-relaxed text-[#111827]">{t}</span>
      </li>
    ))}
  </ol>
);
